import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button, useToast, useConfirm } from '@invested/ui';
import * as api from '../../services/api';
import { ApiError } from '../../services/api';
import type { Checkpoint, CheckpointType, Project, ProjectStatus, Runbook } from '../../types';
import {
  CHECKPOINT_STATUS_META,
  CHECKPOINT_TYPE_META,
  PROJECT_STATUS_META,
  PROJECT_STATUS_ORDER,
} from '../../lib/statusMeta';
import { NotificationConfig } from './NotificationConfig';

interface Props {
  projectId: string;
  onChanged: () => void;
  onDeleted: () => void;
}

const RUNBOOK_POLL_MS = 20000;

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * Inline detail panel — renders expanded-in-place under the project row (no
 * modal). Supports inline editing of project fields and safe runbook sync
 * (live poll + stale-save protection so Codex edits are never clobbered).
 */
export function ProjectDetailPanel({ projectId, onChanged, onDeleted }: Props) {
  const toast = useToast();
  const confirm = useConfirm();
  const [project, setProject] = useState<Project | null>(null);
  const [runbook, setRunbook] = useState<Runbook | null>(null);

  // Project field editing
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaDraft, setMetaDraft] = useState({ category: '', currentStage: '', description: '', copyablePrompt: '' });
  const [savingMeta, setSavingMeta] = useState(false);

  // Checkpoints
  const [newCheckpoint, setNewCheckpoint] = useState('');
  const [newType, setNewType] = useState<CheckpointType>('waiting_for');
  const [addingCp, setAddingCp] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [noteEditId, setNoteEditId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  // Runbook editing + conflict
  const [editingRunbook, setEditingRunbook] = useState(false);
  const [runbookDraft, setRunbookDraft] = useState('');
  const [savingRunbook, setSavingRunbook] = useState(false);
  const [staleConflict, setStaleConflict] = useState(false);
  const editingRunbookRef = useRef(false);
  editingRunbookRef.current = editingRunbook;

  const loadProject = useCallback(async () => {
    const p = await api.getProject(projectId);
    setProject(p);
  }, [projectId]);

  const loadRunbook = useCallback(async () => {
    const rb = await api.getRunbook(projectId);
    setRunbook(rb);
    return rb;
  }, [projectId]);

  useEffect(() => {
    void Promise.all([loadProject(), loadRunbook()]).catch(() => toast.danger('Kunne ikke laste prosjekt.'));
  }, [loadProject, loadRunbook, toast]);

  // Live sync: while the row is open and the runbook editor is NOT active,
  // poll the file so Codex/CLI edits appear without reopening.
  useEffect(() => {
    const timer = setInterval(() => {
      if (editingRunbookRef.current) return;
      void api
        .getRunbook(projectId)
        .then((rb) => setRunbook((prev) => (prev && prev.content === rb.content && prev.modifiedAt === rb.modifiedAt ? prev : rb)))
        .catch(() => undefined);
    }, RUNBOOK_POLL_MS);
    return () => clearInterval(timer);
  }, [projectId]);

  const refresh = useCallback(async () => {
    await loadProject();
    onChanged();
  }, [loadProject, onChanged]);

  // --- Project field editing -------------------------------------------
  async function setStatus(status: ProjectStatus) {
    if (!project || status === project.status) return;
    setProject({ ...project, status });
    try {
      await api.updateProject(projectId, { status });
      onChanged();
    } catch {
      toast.danger('Kunne ikke endre status.');
      await refresh();
    }
  }

  function startEditMeta() {
    if (!project) return;
    setMetaDraft({
      category: project.category,
      currentStage: project.currentStage ?? '',
      description: project.description ?? '',
      copyablePrompt: project.copyablePrompt ?? '',
    });
    setEditingMeta(true);
  }

  async function saveMeta() {
    if (!project) return;
    setSavingMeta(true);
    try {
      await api.updateProject(projectId, {
        category: metaDraft.category.trim() || project.category,
        currentStage: metaDraft.currentStage.trim() || null,
        description: metaDraft.description.trim() || null,
        copyablePrompt: metaDraft.copyablePrompt.trim() || null,
      });
      setEditingMeta(false);
      await refresh();
      toast.success('Prosjekt oppdatert.');
    } catch {
      toast.danger('Kunne ikke lagre endringene.');
    } finally {
      setSavingMeta(false);
    }
  }

  async function deleteProject() {
    if (!project) return;
    const ok = await confirm({
      title: 'Slette prosjektet?',
      description: `«${project.name}» fjernes fra listen. Runbook-fila på disk beholdes.`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteProject(projectId);
      toast.success('Prosjekt slettet.');
      onDeleted();
    } catch {
      toast.danger('Kunne ikke slette prosjektet.');
    }
  }

  // --- Checkpoints ------------------------------------------------------
  async function addCheckpoint() {
    if (!newCheckpoint.trim() || addingCp) return;
    setAddingCp(true);
    try {
      await api.createCheckpoint({ projectId, title: newCheckpoint.trim(), type: newType });
      setNewCheckpoint('');
      await refresh();
    } catch {
      toast.danger('Kunne ikke legge til sjekkpunkt.');
    } finally {
      setAddingCp(false);
    }
  }

  async function toggleResolve(cp: Checkpoint) {
    try {
      if (cp.status === 'resolved') {
        await api.updateCheckpoint(cp.id, { status: 'open' });
      } else {
        await api.resolveCheckpoint(cp.id);
      }
      await refresh();
    } catch {
      toast.danger('Kunne ikke oppdatere sjekkpunkt.');
    }
  }

  async function removeCheckpoint(cp: Checkpoint) {
    const ok = await confirm({ title: 'Slette sjekkpunkt?', description: cp.title, tone: 'danger' });
    if (!ok) return;
    await api.deleteCheckpoint(cp.id);
    await refresh();
  }

  function startNote(cp: Checkpoint) {
    setNoteEditId(cp.id);
    setNoteDraft(cp.notes ?? '');
  }

  async function saveNote(cp: Checkpoint) {
    try {
      await api.updateCheckpoint(cp.id, { notes: noteDraft.trim() || null });
      setNoteEditId(null);
      await refresh();
    } catch {
      toast.danger('Kunne ikke lagre notat.');
    }
  }

  // --- Runbook ----------------------------------------------------------
  function startEditRunbook() {
    setRunbookDraft(runbook?.content ?? '');
    setStaleConflict(false);
    setEditingRunbook(true);
  }

  async function saveRunbook(opts: { force?: boolean } = {}) {
    if (savingRunbook) return;
    setSavingRunbook(true);
    try {
      const res = await api.saveRunbook(projectId, runbookDraft, {
        expectedModifiedAt: runbook?.modifiedAt ?? null,
        force: opts.force,
      });
      setRunbook((prev) => (prev ? { ...prev, content: runbookDraft, exists: true, modifiedAt: res.modifiedAt } : prev));
      setEditingRunbook(false);
      setStaleConflict(false);
      toast.success('Runbook lagret.');
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'runbook_stale' || err.status === 409)) {
        // Codex edited the file while we were typing — reload disk copy for
        // reference, keep the user's draft, and let them choose.
        await loadRunbook();
        setStaleConflict(true);
        toast.danger('Runbooken ble endret av Codex mens du redigerte.');
      } else {
        toast.danger('Kunne ikke lagre runbook.');
      }
    } finally {
      setSavingRunbook(false);
    }
  }

  async function refreshRunbook() {
    try {
      await loadRunbook();
      toast.success('Runbook oppdatert fra disk.');
    } catch {
      toast.danger('Kunne ikke hente runbook.');
    }
  }

  function copyPrompt() {
    if (!project?.copyablePrompt) return;
    void navigator.clipboard.writeText(project.copyablePrompt).then(
      () => toast.success('Prompt kopiert.'),
      () => toast.danger('Kunne ikke kopiere.'),
    );
  }

  if (!project) {
    return <p className="px-4 py-3 text-sm text-black/40">Laster…</p>;
  }

  const allCheckpoints = project.checkpoints ?? [];
  const openCheckpoints = allCheckpoints.filter((c) => c.status !== 'resolved');
  const resolvedCount = allCheckpoints.length - openCheckpoints.length;
  const visibleCheckpoints = showResolved ? allCheckpoints : openCheckpoints;

  return (
    <div className="space-y-6 border-t border-black/10 bg-black/[0.015] px-4 py-4 text-[#0f172a]">
      {/* Header meta — status is always-live; a Rediger toggle exposes the rest */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-black/55">
        <select
          value={project.status}
          onChange={(e) => void setStatus(e.target.value as ProjectStatus)}
          aria-label="Status"
          className={`rounded-full border px-2 py-0.5 font-semibold ${PROJECT_STATUS_META[project.status].className}`}
        >
          {PROJECT_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {PROJECT_STATUS_META[s].label}
            </option>
          ))}
        </select>
        <span>{project.category}</span>
        <span className="font-mono text-black/40">{project.slug}</span>
        {!editingMeta && (
          <button type="button" onClick={startEditMeta} className="ml-auto text-black/45 underline hover:text-black/70">
            Rediger felt
          </button>
        )}
      </div>

      {editingMeta ? (
        <section className="space-y-3 rounded-xl border border-black/10 bg-white p-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-black/45">
            Kategori
            <input
              value={metaDraft.category}
              onChange={(e) => setMetaDraft((d) => ({ ...d, category: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-1.5 text-sm font-normal normal-case text-black outline-none focus:border-black/30"
            />
          </label>
          <label className="block text-xs font-semibold uppercase tracking-wide text-black/45">
            Nåværende steg
            <textarea
              value={metaDraft.currentStage}
              onChange={(e) => setMetaDraft((d) => ({ ...d, currentStage: e.target.value }))}
              rows={2}
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-1.5 text-sm font-normal normal-case text-black outline-none focus:border-black/30"
            />
          </label>
          <label className="block text-xs font-semibold uppercase tracking-wide text-black/45">
            Beskrivelse
            <textarea
              value={metaDraft.description}
              onChange={(e) => setMetaDraft((d) => ({ ...d, description: e.target.value }))}
              rows={2}
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-1.5 text-sm font-normal normal-case text-black outline-none focus:border-black/30"
            />
          </label>
          <label className="block text-xs font-semibold uppercase tracking-wide text-black/45">
            Kopierbar prompt
            <textarea
              value={metaDraft.copyablePrompt}
              onChange={(e) => setMetaDraft((d) => ({ ...d, copyablePrompt: e.target.value }))}
              rows={4}
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-1.5 font-mono text-xs font-normal normal-case text-black outline-none focus:border-black/30"
            />
          </label>
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => void deleteProject()} className="text-xs text-red-600 hover:underline">
              Slett prosjekt
            </button>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditingMeta(false)} disabled={savingMeta}>
                Avbryt
              </Button>
              <Button size="sm" onClick={() => void saveMeta()} disabled={savingMeta}>
                {savingMeta ? 'Lagrer…' : 'Lagre'}
              </Button>
            </div>
          </div>
        </section>
      ) : (
        <>
          {project.currentStage && (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-black/45">Nåværende steg</h3>
              <p className="text-sm leading-6">{project.currentStage}</p>
            </section>
          )}
          {project.description && (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-black/45">Beskrivelse</h3>
              <p className="text-sm leading-6 text-black/70">{project.description}</p>
            </section>
          )}
          {project.copyablePrompt && (
            <section className="rounded-xl border border-black/10 bg-white p-3">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-black/45">Kopierbar prompt</h3>
                <Button size="sm" onClick={copyPrompt}>
                  Kopier
                </Button>
              </div>
              <pre className="whitespace-pre-wrap break-words text-sm text-black/70">{project.copyablePrompt}</pre>
            </section>
          )}
        </>
      )}

      {/* Checkpoints */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-black/45">
            Sjekkpunkter ({openCheckpoints.length} åpne)
          </h3>
          {resolvedCount > 0 && (
            <button type="button" onClick={() => setShowResolved((v) => !v)} className="text-xs text-black/45 hover:text-black/70">
              {showResolved ? 'Skjul løste' : `Vis løste (${resolvedCount})`}
            </button>
          )}
        </div>
        <ul className="space-y-1">
          {visibleCheckpoints.map((cp) => (
            <li key={cp.id} className="rounded-lg border border-black/[0.07] bg-white px-2 py-1.5 text-sm">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={cp.status === 'resolved'}
                  onChange={() => void toggleResolve(cp)}
                  className="mt-1 h-4 w-4 accent-emerald-600"
                  aria-label={cp.status === 'resolved' ? 'Gjenåpne' : 'Løs'}
                />
                <span className="mt-0.5" title={CHECKPOINT_TYPE_META[cp.type].label}>
                  {CHECKPOINT_TYPE_META[cp.type].icon}
                </span>
                <span className={`flex-1 ${CHECKPOINT_STATUS_META[cp.status].className}`}>{cp.title}</span>
                <button
                  type="button"
                  onClick={() => (noteEditId === cp.id ? setNoteEditId(null) : startNote(cp))}
                  className="px-1 text-xs text-black/35 hover:text-black/70"
                  title={cp.notes ? 'Rediger notat/utfall' : 'Legg til notat/utfall'}
                >
                  {cp.notes ? '✎' : '＋notat'}
                </button>
                <button
                  type="button"
                  onClick={() => void removeCheckpoint(cp)}
                  className="px-1 text-black/30 hover:text-red-600"
                  aria-label="Slett sjekkpunkt"
                >
                  ×
                </button>
              </div>
              {noteEditId === cp.id ? (
                <div className="mt-1.5 flex gap-2 pl-6">
                  <input
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && void saveNote(cp)}
                    placeholder="Notat / hva kom / utfall…"
                    aria-label="Notat"
                    autoFocus
                    className="flex-1 rounded-lg border border-black/15 px-2 py-1 text-xs outline-none focus:border-black/30"
                  />
                  <Button size="sm" variant="secondary" onClick={() => void saveNote(cp)}>
                    Lagre
                  </Button>
                </div>
              ) : (
                cp.notes && <p className="mt-0.5 pl-6 text-xs text-black/50">↳ {cp.notes}</p>
              )}
            </li>
          ))}
          {visibleCheckpoints.length === 0 && <li className="text-xs text-black/40">Ingen åpne sjekkpunkter.</li>}
        </ul>
        <div className="mt-2 flex gap-2">
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value as CheckpointType)}
            aria-label="Type sjekkpunkt"
            className="rounded-lg border border-black/15 bg-white px-2 py-1.5 text-sm"
          >
            <option value="blocker">🔴 Blocker</option>
            <option value="waiting_for">⏳ Venter på</option>
            <option value="todo">⬜ To-do</option>
          </select>
          <input
            value={newCheckpoint}
            onChange={(e) => setNewCheckpoint(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void addCheckpoint()}
            placeholder="Nytt sjekkpunkt…"
            aria-label="Nytt sjekkpunkt"
            className="flex-1 rounded-lg border border-black/15 bg-white px-3 py-1.5 text-sm outline-none focus:border-black/30"
          />
          <Button size="sm" variant="secondary" onClick={() => void addCheckpoint()} disabled={addingCp}>
            Legg til
          </Button>
        </div>
      </section>

      {/* Runbook */}
      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-black/45">
            Runbook <span className="ml-1 font-normal normal-case text-black/35">sist endret {formatTime(runbook?.modifiedAt ?? null)}</span>
          </h3>
          {editingRunbook ? (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setEditingRunbook(false); setStaleConflict(false); }} disabled={savingRunbook}>
                Avbryt
              </Button>
              <Button size="sm" onClick={() => void saveRunbook()} disabled={savingRunbook}>
                {savingRunbook ? 'Lagrer…' : 'Lagre'}
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => void refreshRunbook()}>
                Oppdater
              </Button>
              <Button size="sm" variant="secondary" onClick={startEditRunbook}>
                Rediger
              </Button>
            </div>
          )}
        </div>

        {staleConflict && editingRunbook && (
          <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p className="font-semibold">Codex endret runbooken mens du redigerte.</p>
            <p className="mt-0.5">Din versjon er ikke lagret. Velg hva du vil gjøre:</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => void saveRunbook({ force: true })} disabled={savingRunbook}>
                Overskriv med min versjon
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setRunbookDraft(runbook?.content ?? ''); setStaleConflict(false); }}
              >
                Forkast mine — hent Codex sin
              </Button>
            </div>
          </div>
        )}

        {editingRunbook ? (
          <textarea
            value={runbookDraft}
            onChange={(e) => setRunbookDraft(e.target.value)}
            aria-label="Runbook markdown"
            className="h-72 w-full rounded-xl border border-black/15 bg-white p-3 font-mono text-xs outline-none focus:border-black/30"
          />
        ) : runbook?.content ? (
          <div className="prose prose-sm max-h-80 max-w-none overflow-y-auto rounded-xl border border-black/10 bg-white p-4">
            <ReactMarkdown>{runbook.content}</ReactMarkdown>
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-black/15 p-4 text-xs text-black/40">
            Ingen runbook ennå. Klikk Rediger for å opprette.
          </p>
        )}
      </section>

      {/* Notifications */}
      <NotificationConfig projectId={project.id} />
    </div>
  );
}
